import React, { FC, useEffect, useState } from 'react';
import {
  getInitialSettlementsNavTab, getSettlementsBaseUrls,
  SETTLEMENTS_TABS,
  settlementsTabs,
} from '../../routing/settlementsRoutes';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { ISettlementsFilters, SettlementsFilterSection } from '../../components/settlements/SettlementsFilterSection';
import { PageTitle } from '../../layout/core';
import { fundsBreadCrumbs } from '../funds-reserve/FundsReservePage';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { fetchWhiteListedPartnersList } from '../../store/merchnts/actions/partners';
import { IPaginationData } from '../../interfaces/funds';
import {
  fetchLatestSettlementsData,
  fetchPartnerSettlementsData,
  fetchSettlementsData,
} from '../../store/payments/actions/settlements';

interface Props {
  children: React.ReactElement;
}
export const SettlementsPage: FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
  const isSuperAdmin = checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN);

  const { merchantState: { chosenMerchant }, dispatch: merchantDispatch } = useMerchants();

  const [batchType, setBatchType] = useState<string>('');
  const [partnerId, setPartnerId] = useState<string>('');
  const [activeNavTab, setActiveNavTab] = React.useState<SETTLEMENTS_TABS>(getInitialSettlementsNavTab(pathname, isSuperAdmin));
  const [loading, setLoading] = useState<boolean>(true);
  const [latestSettlementsData, setLatestSettlementsData] = useState<IPaginationData>(null);
  const [settlementsData, setSettlementsData] = useState<IPaginationData>(null);

  const filtersFromUrl: ISettlementsFilters = parseSearchParams(search) || {};

  useEffect(() => {
    if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
      void fetchWhiteListedPartnersList(merchantDispatch);
    }
  }, []);

  const handleTabChange = (tab: SETTLEMENTS_TABS): void => {
    setActiveNavTab(tab);
    const newFilters: ISettlementsFilters = { ...filtersFromUrl };

    if (filtersFromUrl.pageNumber) {
      delete newFilters.pageNumber;
    }

    if (filtersFromUrl.pageSize) {
      delete newFilters.pageSize;
    }

    navigate(`${getSettlementsBaseUrls(isSuperAdmin)}/${tab}?${makeSearchParams(newFilters)}`);
    setLoading(true);
  };

  const listener = async (searchParams: ISettlementsFilters): Promise<void> => {
    if (!chosenMerchant && !searchParams?.mid) {
      const merchantId = localStorage.getItem('chosenMerchant');
      if (merchantId) {
        return;
      }
    }

    setLoading(true);

    let localSearchParams: ISettlementsFilters;

    if (Object.keys(searchParams).length) {
      localSearchParams = searchParams;
      setBatchType(searchParams?.batchType ?? '');
      setPartnerId(searchParams?.partnerId ?? '');

    } else {
      localSearchParams = {
        mid: chosenMerchant
      };
    }

    switch (activeNavTab) {
      case SETTLEMENTS_TABS.LATEST_SETTLEMENTS: {
        const	latestData = await fetchLatestSettlementsData(localSearchParams);
        setLatestSettlementsData(latestData);
        break;
      }
      case SETTLEMENTS_TABS.ALL_SETTLEMENTS: {
        let data: IPaginationData;

        if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_MERCHANT_FINANCE)) {
          data = await fetchSettlementsData(localSearchParams);
          // TODO check partners permissions
        } else if (checkRoles(authUser, CLAIMS.PARTNER_ADMIN)) {
          data = await fetchPartnerSettlementsData(localSearchParams);
        }

        setSettlementsData(data);
        break;
      }
    }

    setLoading(false);
  };

  return (
    <>
      <PageTitle breadcrumbs={fundsBreadCrumbs}>{t('MENU.SETTLEMENTS')}</PageTitle>

      <SettlementsFilterSection
        batchType={batchType}
        partnerId={partnerId}
        setBatchType={setBatchType}
        setPartnerId={setPartnerId}
      />

      <Navbar
        tabsList={settlementsTabs}
        active={activeNavTab}
        onSelect={handleTabChange}
      />

      {React.cloneElement(children, { listener, latestSettlementsData, settlementsData, isSettlementsDataLoading: loading })}
    </>
  );
};
